import React, { useEffect, useMemo } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { ssrDocument } from '@/utils/ssrUtils';

type Heading = {
  id: string;
  text: string;
  level: number; // 2 or 3
};

interface InBodyTocProps {
  content: string;          // HTML string of the post content
  className?: string;
  title?: string;           // Optional section title
}

/**
 * InBodyToc
 * - Lightweight, on-brand ToC for body (mobile/tablet)
 * - Uses existing Shadcn Accordion (no ScrollArea)
 * - Parses H2/H3 from provided HTML string
 * - Adds IDs to actual DOM headings if missing (so links work reliably)
 * - Intended to complement the sticky sidebar ToC (which remains unchanged)
 */
const InBodyToc: React.FC<InBodyTocProps> = ({ content, className, title = 'Table of Contents' }) => {
  // Parse headings (H2/H3) from HTML string
  const headings = useMemo<Heading[]>(() => {
    if (!content) return [];
    const items: Heading[] = [];
    // Regex to capture h2/h3 (case-insensitive), inner text only (strip tags)
    const regex = /<h([23])([^>]*)>(.*?)<\/h\1>/gims;
    let match: RegExpExecArray | null;
    let index = 0;

    const stripTags = (html: string) => html.replace(/<[^>]*>/g, '').trim();

    while ((match = regex.exec(content)) !== null) {
      const level = parseInt(match[1], 10);
      const text = stripTags(match[3]);
      if (!text) continue;
      // Generate slug id
      let id = text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

      // Ensure uniqueness within this array
      if (items.some(h => h.id === id)) {
        id = `${id}-${index}`;
      }

      items.push({ id, text, level });
      index++;
    }

    return items;
  }, [content]);

  // Group H3 under last seen H2
  const groups = useMemo(() => {
    const out: { id: string; text: string; children: Heading[] }[] = [];
    let current: { id: string; text: string; children: Heading[] } | null = null;

    headings.forEach(h => {
      if (h.level === 2) {
        current = { id: h.id, text: h.text, children: [] };
        out.push(current);
      } else if (h.level === 3 && current) {
        current.children.push(h);
      }
    });

    return out;
  }, [headings]);


  // Ensure actual DOM headings (article h2/h3) have IDs matching parsed list
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!headings.length) return;

    const slugify = (txt: string) =>
      txt.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

    const assignIds = () => {
      const article = ssrDocument.querySelector('article');
      if (!article) return;

      const domHeadings = Array.from(article.querySelectorAll('h2, h3')) as HTMLElement[];
      const used = new Map<string, number>();

      domHeadings.forEach((el) => {
        if (el.id) return; // respect existing IDs (e.g., sidebar ToC setup)
        const base = slugify(el.textContent || '');
        const count = used.get(base) || 0;
        used.set(base, count + 1);
        el.id = count > 0 ? `${base}-${count}` : base;
      });
    };

    // Run now and again shortly after to ensure content is mounted before adding IDs
    assignIds();
    const t = setTimeout(assignIds, 120);
    return () => clearTimeout(t);
  }, [headings]);

  const scrollTo = (id: string) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    const el = ssrDocument.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 80; // adjust for header
    window.scrollTo({ top: y, behavior: 'smooth' });
  };

  if (groups.length === 0) return null;

  return (
    <section
      aria-label="In-article table of contents"
      className={cn(
        'mb-8 rounded-lg border border-gray-800 bg-gray-900/60 p-4',
        'shadow-sm',
        className
      )}
    >
      <h2 className="mb-2 text-sm font-semibold tracking-wide text-gray-300">
        {title}
      </h2>

      {/* Preserve original article order by rendering each H2 in sequence.
          If it has children (H3s), render an Accordion item; otherwise render a simple row. */}
      <div className="space-y-1">
        {groups.map(group => (
          group.children.length === 0 ? (
            <div key={group.id}>
              <button
                onClick={() => scrollTo(group.id)}
                className="w-full rounded px-2 py-2 text-left text-sm text-gray-300 transition-colors hover:bg-gray-800/60 hover:text-white"
              >
                {group.text}
              </button>
            </div>
          ) : (
            <Accordion key={group.id} type="single" collapsible className="divide-y divide-gray-800">
              <AccordionItem value={group.id} className="border-none">
                <AccordionTrigger
                  onClick={() => scrollTo(group.id)}
                  className="py-2 text-left text-sm font-medium text-gray-200 hover:no-underline hover:text-white"
                >
                  {group.text}
                </AccordionTrigger>
                <AccordionContent className="pb-2">
                  <ul className="mt-1 space-y-1">
                    {group.children.map(child => (
                      <li key={child.id}>
                        <button
                          onClick={() => scrollTo(child.id)}
                          className="w-full rounded px-2 py-1 text-left text-xs text-gray-400 transition-colors hover:bg-gray-800/60 hover:text-gray-200"
                        >
                          {child.text}
                        </button>
                      </li>
                    ))}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )
        ))}
      </div>
    </section>
  );
};

export default InBodyToc;
